import React, { useEffect, useState } from "react";
import {
  EmailServerConfigDto,
  getEmailServerConfig,
  updateEmailServerConfig,
  sendTestEmail,
  verifySMTPConnection,
  getAllEmailServers,
  setActiveEmailServer,
  deleteEmailServer,
} from "../../services/emails.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { useNavigate } from "react-router-dom";
import ModalContainer from "../../components/ModalContainer.tsx";
import { notification } from "../../utils/Notifications.tsx";

const serverSchema = Yup.object().shape({
  name: Yup.string().required("Nombre del servidor requerido"),
  host: Yup.string().required("Servidor SMTP requerido"),
  port: Yup.number().required("Puerto requerido").integer().positive(),
  username: Yup.string().nullable(),
  password: Yup.string().nullable(),
});

const EmailSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [serverConfig, setServerConfig] = useState<EmailServerConfigDto | null>(
    null
  );
  const [savingServer, setSavingServer] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [verifyingConnection, setVerifyingConnection] = useState(false);
  const [allServers, setAllServers] = useState<EmailServerConfigDto[]>([]);
  const [deletingServer, setDeletingServer] = useState(false);
  useAppSettings();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [server, servers] = await Promise.all([
        getEmailServerConfig(),
        getAllEmailServers(),
      ]);
      setServerConfig(server);
      setAllServers(servers);
    } catch (error) {
      console.error("Error cargando configuración de correos", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const serverFormik = useFormik<EmailServerConfigDto>({
    enableReinitialize: true,
    initialValues: {
      name: serverConfig?.name ?? "Servidor SMTP",
      host: serverConfig?.host ?? "",
      port: serverConfig?.port ?? 587,
      username: serverConfig?.username ?? "",
      password: "",
    },
    validationSchema: serverSchema,
    onSubmit: async (values) => {
      setSavingServer(true);
      try {
        const verifyPayload: EmailServerConfigDto = {
          host: values.host,
          port: values.port,
          username: values.username,
          password: values.password || serverConfig?.password || undefined,
        };

        const verifyResult = await verifySMTPConnection(verifyPayload);

        if (!verifyResult.success) {
          notification(
            "Error de verificación",
            verifyResult.message ||
              "No se pudo verificar la conexión SMTP. La configuración no se guardó.",
            "danger"
          );
          setSavingServer(false);
          return;
        }

        const payload: EmailServerConfigDto = {
          name: values.name,
          host: values.host,
          port: values.port,
          username: values.username,

          password: values.password || undefined,
        };
        const updated = await updateEmailServerConfig(payload);
        setServerConfig(updated);
        await fetchData();
        notification(
          "Éxito",
          "Configuración verificada y guardada correctamente",
          "success"
        );
      } catch (error: any) {
        console.error("Error guardando servidor SMTP", error);
        const errorMessage =
          error.response?.data?.message || "Error al guardar la configuración";
        notification("Error", errorMessage, "danger");
      } finally {
        setSavingServer(false);
      }
    },
  });

  const handleCancel = () => {
    navigate("/emails/plantillas");
  };

  const handleDeleteServer = async () => {
    if (!serverConfig?.id) return;

    if (
      !window.confirm(
        `¿Estás seguro de eliminar el servidor "${serverConfig.name}"?`
      )
    ) {
      return;
    }

    setDeletingServer(true);
    try {
      await deleteEmailServer(serverConfig.id);
      notification("Éxito", "Servidor eliminado correctamente", "success");
      await fetchData();
    } catch (error: any) {
      console.error("Error eliminando servidor", error);
      notification("Error", "No se pudo eliminar el servidor", "danger");
    } finally {
      setDeletingServer(false);
    }
  };

  const handleToggleActiveServer = async (serverId: number) => {
    try {
      if (serverConfig?.id === serverId) {
        const otherServers = allServers.filter((s) => s.id !== serverId);
        if (otherServers.length > 0) {
          await setActiveEmailServer(otherServers[0].id!);
          notification(
            "Éxito",
            "Servidor desvinculado y otro servidor activado",
            "success"
          );
        } else {
          notification(
            "Aviso",
            "No se puede desvincular si es el único servidor",
            "warning"
          );
          return;
        }
      } else {
        await setActiveEmailServer(serverId);
        notification("Éxito", "Servidor vinculado correctamente", "success");
      }
      await fetchData();
    } catch (error: any) {
      console.error("Error cambiando servidor activo", error);
      notification("Error", "No se pudo cambiar el servidor activo", "danger");
    }
  };

  const handleVerifyConnection = async () => {
    setVerifyingConnection(true);
    try {
      const result = await verifySMTPConnection();
      if (result.success) {
        notification(
          "Éxito",
          result.message || "Conexión SMTP verificada correctamente",
          "success"
        );
      } else {
        notification(
          "Error",
          result.message || "No se pudo verificar la conexión SMTP",
          "danger"
        );
      }
    } catch (error: any) {
      console.error("Error verificando conexión SMTP", error);
      notification(
        "Error",
        error.response?.data?.message ||
          "No se pudo verificar la conexión SMTP",
        "danger"
      );
    } finally {
      setVerifyingConnection(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      notification("Aviso", "Ingresa un correo de destino", "warning");
      return;
    }
    setSendingTest(true);
    try {
      await sendTestEmail(testEmail);
      notification(
        "Éxito",
        `Correo de prueba enviado a ${testEmail}`,
        "success"
      );
      setShowTestModal(false);
      setTestEmail("");
    } catch (error) {
      console.error("Error enviando correo de prueba", error);
      notification("Error", "No se pudo enviar el correo de prueba", "danger");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Layout loading={loading}>
      <div className="border-0 m-4 rounded">
        <Title
          baseTitle="Correos"
          basePath="/emails/plantillas"
          activeTitle="Servidor SMTP"
          title="Servidor SMTP"
        />

        <div style={styles.headerToolbar}>
          <p style={styles.subtitle}>
            Configura el servidor de correo electrónico.
          </p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleVerifyConnection}
              disabled={verifyingConnection}
              style={{
                ...styles.verifyButton,
                opacity: verifyingConnection ? 0.6 : 1,
              }}
              title="Verificar conexión SMTP"
            >
              <i className="fa-solid fa-plug" style={{ fontSize: "18px" }}></i>
            </button>
            <button
              onClick={() => setShowTestModal(true)}
              style={styles.testButton}
              title="Probar envío de correo"
            >
              <i className="fa-solid fa-gear" style={{ fontSize: "18px" }}></i>
            </button>
          </div>
        </div>

        <div style={styles.content}>
          <div style={styles.leftSection}>
            <form onSubmit={serverFormik.handleSubmit}>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>
                    Servidor SMTP y autenticación
                  </h2>
                </div>
                <div style={styles.cardBody}>
                  <div>
                    <label style={styles.label}>Nombre del servidor</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="ej: Gmail SMTP"
                      {...serverFormik.getFieldProps("name")}
                    />
                  </div>

                  <div style={styles.row}>
                    <div style={{ flex: 2 }}>
                      <label style={styles.label}>Host *</label>
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          borderColor:
                            serverFormik.touched.host &&
                            serverFormik.errors.host
                              ? "#f56565"
                              : "#e2e8f0",
                        }}
                        placeholder="smtp.gmail.com"
                        {...serverFormik.getFieldProps("host")}
                      />
                      {serverFormik.touched.host &&
                        serverFormik.errors.host && (
                          <div style={styles.error}>
                            {serverFormik.errors.host}
                          </div>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Puerto *</label>
                      <input
                        type="number"
                        style={styles.input}
                        placeholder="587"
                        {...serverFormik.getFieldProps("port")}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={styles.label}>Usuario</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="tu-email@ejemplo.com"
                      {...serverFormik.getFieldProps("username")}
                    />
                  </div>

                  <div>
                    <label style={styles.label}>Contraseña</label>
                    <input
                      type="password"
                      style={styles.input}
                      placeholder="••••••••"
                      {...serverFormik.getFieldProps("password")}
                    />
                    <div style={styles.hint}>
                      Déjalo vacío para mantener la contraseña actual
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.footer}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={styles.cancelButton}
                  disabled={savingServer}
                >
                  <i
                    className="fa-solid fa-arrow-left"
                    style={{ fontSize: "18px" }}
                  ></i>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => serverFormik.handleSubmit()}
                  style={{
                    ...styles.submitButton,
                    opacity: savingServer ? 0.6 : 1,
                  }}
                  disabled={savingServer}
                >
                  <i
                    className="fa-solid fa-save"
                    style={{ fontSize: "18px" }}
                  ></i>
                  {savingServer ? "Guardando..." : "Guardar Configuración"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Información del servidor conectado */}
        {serverConfig && (
          <div style={styles.serverInfoCard}>
            <div style={styles.serverInfoHeader}>
              <div>
                <h3 style={styles.serverInfoTitle}>Servidor conectado</h3>
                <p style={styles.serverInfoSubtitle}>
                  {serverConfig.name} - {serverConfig.host}:{serverConfig.port}
                </p>
              </div>
              <div style={styles.serverInfoBadge}>
                <i
                  className="fa-solid fa-check-circle"
                  style={{ fontSize: "16px" }}
                ></i>
                <span>Activo</span>
              </div>
            </div>
            <div style={styles.serverInfoActions}>
              {allServers.length > 1 && (
                <button
                  onClick={() => {
                    const otherServer = allServers.find(
                      (s) => s.id !== serverConfig.id
                    );
                    if (otherServer?.id) {
                      handleToggleActiveServer(otherServer.id);
                    }
                  }}
                  style={styles.unlinkButton}
                  title="Desvincular y activar otro servidor"
                >
                  <i
                    className="fa-solid fa-unlink"
                    style={{ fontSize: "16px" }}
                  ></i>
                  Desvincular
                </button>
              )}
              {allServers.length > 1 && (
                <div
                  style={styles.linkDropdown}
                  onMouseEnter={(e) => {
                    const dropdown = e.currentTarget.querySelector(
                      "[data-dropdown]"
                    ) as HTMLElement;
                    if (dropdown) dropdown.style.display = "block";
                  }}
                  onMouseLeave={(e) => {
                    const dropdown = e.currentTarget.querySelector(
                      "[data-dropdown]"
                    ) as HTMLElement;
                    if (dropdown) dropdown.style.display = "none";
                  }}
                >
                  <button
                    style={styles.linkButton}
                    title="Vincular otro servidor"
                  >
                    <i
                      className="fa-solid fa-link"
                      style={{ fontSize: "16px" }}
                    ></i>
                    Vincular otro
                  </button>
                  <div style={styles.dropdownContent} data-dropdown>
                    {allServers
                      .filter((s) => s.id !== serverConfig.id)
                      .map((server) => (
                        <div
                          key={server.id}
                          style={styles.dropdownItem}
                          onClick={() => handleToggleActiveServer(server.id!)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f7fafc";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                          }}
                        >
                          <div>
                            <div style={styles.dropdownItemName}>
                              {server.name}
                            </div>
                            <div style={styles.dropdownItemHost}>
                              {server.host}:{server.port}
                            </div>
                          </div>
                          {server.active === false && (
                            <i
                              className="fa-solid fa-circle"
                              style={{ fontSize: "8px", color: "#cbd5e0" }}
                            ></i>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
              <button
                onClick={handleDeleteServer}
                disabled={deletingServer}
                style={{
                  ...styles.deleteButton,
                  opacity: deletingServer ? 0.6 : 1,
                }}
                title="Eliminar servidor"
              >
                <i
                  className="fa-solid fa-trash"
                  style={{ fontSize: "16px" }}
                ></i>
                {deletingServer ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}

        <ModalContainer
          open={showTestModal}
          closeFn={() => {
            setShowTestModal(false);
            setTestEmail("");
          }}
          title="Probar envío de correo"
        >
          <div className="mb-3">
            <label className="form-label">Correo de destino</label>
            <input
              type="email"
              className="form-control"
              placeholder="correo@ejemplo.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={handleSendTest}
            disabled={sendingTest || !testEmail}
          >
            {sendingTest ? "Enviando..." : "Enviar correo de prueba"}
          </button>
        </ModalContainer>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "24px 32px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  headerToolbar: {
    marginTop: "8px",
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },
  verifyButton: {
    background: "#48bb78",
    color: "#fff",
    border: "none",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 16px rgba(72, 187, 120, 0.3)",
  },
  testButton: {
    background: "#50B7C0",
    color: "#fff",
    border: "none",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 16px rgba(80, 183, 192, 0.3)",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "16px",
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
    marginTop: "16px",
  },
  cancelButton: {
    background: "#fff",
    color: "#718096",
    border: "2px solid #e2e8f0",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },
  submitButton: {
    background: "#50B7C0",
    color: "#fff",
    border: "none",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 16px rgba(80, 183, 192, 0.3)",
  },
  content: {
    marginTop: "8px",
  },
  leftSection: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  rightSection: {
    display: "none",
  },
  card: {
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "10px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background: "linear-gradient(135deg, #f7fafc 0%, #ffffff 100%)",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2d3748",
    margin: 0,
  },
  cardBody: {
    padding: "12px 16px 14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  row: {
    display: "flex",
    gap: "8px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    marginBottom: "8px",
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    fontSize: "13px",
    border: "2px solid #e2e8f0",
    borderRadius: "6px",
    outline: "none",
    transition: "all 0.3s ease",
    background: "#fff",
  },
  hint: {
    fontSize: "12px",
    color: "#718096",
    marginTop: "6px",
    fontStyle: "italic",
  },
  error: {
    fontSize: "12px",
    color: "#f56565",
    marginTop: "6px",
    fontWeight: "500",
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "16px",
    background: "#f7fafc",
    borderRadius: "12px",
    transition: "all 0.3s ease",
  },
  switchLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  switchLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#2d3748",
  },
  switchHint: {
    fontSize: "12px",
    color: "#718096",
    marginTop: "2px",
  },
  toggle: {
    width: "56px",
    height: "32px",
    borderRadius: "16px",
    position: "relative" as const,
    transition: "all 0.3s ease",
  },
  toggleCircle: {
    width: "28px",
    height: "28px",
    background: "#fff",
    borderRadius: "50%",
    position: "absolute" as const,
    top: "2px",
    transition: "transform 0.3s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
  },
  testCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
  testDescription: {
    fontSize: "14px",
    color: "#718096",
    lineHeight: "1.6",
    margin: "0",
  },
  successMessage: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    background: "rgba(72, 187, 120, 0.1)",
    border: "1px solid rgba(72, 187, 120, 0.3)",
    borderRadius: "12px",
    color: "#38a169",
  },
  errorMessage: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    background: "rgba(245, 101, 101, 0.1)",
    border: "1px solid rgba(245, 101, 101, 0.3)",
    borderRadius: "12px",
    color: "#e53e3e",
  },
  statusTitle: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "2px",
  },
  statusText: {
    fontSize: "12px",
    opacity: 0.9,
  },
  infoCard: {
    background: "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #e2e8f0",
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "16px",
  },
  infoTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#2d3748",
    margin: 0,
  },
  infoContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  infoItem: {
    fontSize: "13px",
    color: "#4a5568",
    lineHeight: "1.6",
  },
  serverInfoCard: {
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    padding: "16px 20px",
    marginTop: "16px",
    border: "1px solid #e2e8f0",
  },
  serverInfoHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  serverInfoTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#2d3748",
    margin: "0 0 4px 0",
  },
  serverInfoSubtitle: {
    fontSize: "13px",
    color: "#718096",
    margin: 0,
  },
  serverInfoBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(72, 187, 120, 0.1)",
    color: "#48bb78",
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
  },
  serverInfoActions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  unlinkButton: {
    background: "#f56565",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.3s ease",
  },
  linkButton: {
    background: "#50B7C0",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.3s ease",
  },
  linkDropdown: {
    position: "relative" as const,
  },
  dropdownContent: {
    display: "none",
    position: "absolute" as const,
    top: "100%",
    left: 0,
    marginTop: "4px",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e2e8f0",
    minWidth: "250px",
    zIndex: 1000,
  },
  dropdownItem: {
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "background 0.2s ease",
  },
  dropdownItemName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#2d3748",
    marginBottom: "2px",
  },
  dropdownItemHost: {
    fontSize: "12px",
    color: "#718096",
  },
  deleteButton: {
    background: "#f56565",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.3s ease",
  },
};

export default EmailSettingsPage;
